import { CollegeProductionRow } from '../types/CollegeProductionData';
import Player from '../types/Player';
import { OffensivePosition } from '../types/Position';

export type RankingType = 'passing' | 'rushing' | 'receiving' | 'defense' | 'punting' | 'kicking';

export interface RankingLink {
  type: RankingType;
  label: string;
  url: string;
}

export interface PlayerRankingLinks {
  primary?: RankingLink;
  additional: RankingLink[];
  all: RankingLink[];
}

const CBTN_BASE_URL = 'https://coachesbythenumbers.com';

const RANKING_DEFINITIONS: Array<{ type: RankingType; label: string; path: string }> = [
  { type: 'passing', label: 'Passing Rankings', path: '/player-passing-analytics/' },
  { type: 'rushing', label: 'Rushing Rankings', path: '/player-rushing-analytics/' },
  { type: 'receiving', label: 'Receiving Rankings', path: '/player-receiving-analytics/' },
  { type: 'defense', label: 'Defense Rankings', path: '/player-defense-analytics/' },
  { type: 'punting', label: 'Punting Rankings', path: '/player-punting-analytics/' },
  { type: 'kicking', label: 'Kicking Rankings', path: '/player-kicking-analytics/' },
];

function toNumber(value: unknown): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function hasKickAttempt(row: CollegeProductionRow): boolean {
  const record = row as unknown as Record<string, unknown>;
  const attempts =
    // toNumber(record.kickingXPA) +
    toNumber(record.kickingXPAtt) +
    // toNumber(record.kickingFGA) +
    toNumber(record.kickingFGAtt) +
    toNumber(record.kickoff)

  return attempts >= 1;
}

function hasOffensivePrimaryCollegePosition(player?: Player): boolean {
  const primaryCollege = (player?.playerColleges || []).find((college) => Boolean(college?.isPrimary));
  const primaryCollegePositions = (primaryCollege?.positions || []).map((position) => String(position?.code || '').trim().toUpperCase());
  const offensivePositionCodes = new Set<string>(Object.values(OffensivePosition));

  return primaryCollegePositions.some((positionCode) => offensivePositionCodes.has(positionCode));
}

export function buildPlayerRankingLinks(player?: Player, rows: CollegeProductionRow[] = []): PlayerRankingLinks {
  if (!rows.length) {
    return { primary: undefined, additional: [], all: [] };
  }

  const mostRecentYear = rows.reduce((maxYear, row) => {
    const year = toNumber(row.year);
    return year > maxYear ? year : maxYear;
  }, Number.NEGATIVE_INFINITY);

  const mostRecentYearRows = rows.filter((row) => toNumber(row.year) === mostRecentYear);

  const totals = mostRecentYearRows.reduce(
    (acc, row) => {
      acc.passingAtt += toNumber(row.passingAtt);
      acc.rushingAtt += toNumber(row.rushingAtt);
      acc.receivingTar += toNumber(row.receivingTar);
      acc.tackles += toNumber(row.defenseSoloTackles) + toNumber(row.defenseAssistedTackles);
      acc.punt += toNumber(row.punt);
      acc.kickAttempts += hasKickAttempt(row) ? 1 : 0;
      return acc;
    },
    {
      passingAtt: 0,
      rushingAtt: 0,
      receivingTar: 0,
      tackles: 0,
      punt: 0,
      kickAttempts: 0,
    },
  );

  const qualifies: Record<RankingType, boolean> = {
    passing: totals.passingAtt >= 5,
    rushing: totals.rushingAtt >= 5,
    receiving: totals.receivingTar >= 5,
    defense: !hasOffensivePrimaryCollegePosition(player) && totals.tackles >= 1,
    punting: totals.punt >= 1,
    kicking: totals.kickAttempts >= 1,
  };

  const all = RANKING_DEFINITIONS
    .filter((definition) => qualifies[definition.type])
    .map((definition) => ({
      type: definition.type,
      label: definition.label,
      url: `${CBTN_BASE_URL}${definition.path}`,
    }));

  if (!all.length) {
    return { primary: undefined, additional: [], all };
  }

  const primary = all[0];
  const additional = all.slice(1);

  return { primary, additional, all };
}
